import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { partnerSettingsService } from "@/services/partner-settings-service";
import type { PartnerRevenueDateRange } from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";
import {
  buildRapidTotalRevenueMetric,
  type ServicesRapidRevenueMetric,
} from "../components/services/servicesRapidMappers";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

export const rapidPartnersRevenueKeys = {
  all: ["analytics-revenue", "rapid-partners"] as const,
  earned: (hotelId: string, from: string, to: string) =>
    [...rapidPartnersRevenueKeys.all, "earned", hotelId, from, to] as const,
};

// DV-87: Earned is the hotel's share of the commission on orders whose service
// date has passed (partner orders-summary `earnings`, the commission share
// model). The revenue distribution sums the guest price of orders, which is
// not what the hotel earned. Future and not yet earned amounts stay in
// `pendingEarnings` ("Expected"); the summary needs no clamp at today.
export function useRapidPartnersRevenueKpi(range: PartnerRevenueDateRange) {
  // Hotel scope follows the analytics page (route hotel for platform admins).
  const rangeParams = useAnalyticsOverviewRange(range);
  const { hotelId, from, to, isReady } = rangeParams;
  const previousRange = useMemo(
    () => getPreviousAnalyticsDateRange(range),
    [range],
  );
  const previousFrom = toAnalyticsDateParam(previousRange?.from);
  const previousTo = toAnalyticsDateParam(previousRange?.to);
  const hasPreviousRange = Boolean(previousFrom && previousTo);

  const current = useQuery({
    queryKey: rapidPartnersRevenueKeys.earned(hotelId, from, to),
    queryFn: () =>
      partnerSettingsService.getPartnerOrdersSummary(hotelId, { from, to }),
    enabled: isReady,
  });

  const previous = useQuery({
    queryKey: rapidPartnersRevenueKeys.earned(
      hotelId,
      previousFrom ?? "",
      previousTo ?? "",
    ),
    queryFn: () =>
      partnerSettingsService.getPartnerOrdersSummary(hotelId, {
        from: previousFrom!,
        to: previousTo!,
      }),
    enabled: isReady && hasPreviousRange,
  });

  const metric: ServicesRapidRevenueMetric = useMemo(
    () =>
      buildRapidTotalRevenueMetric({
        currentEarnings: current.data?.earnings,
        previousEarnings: previous.data?.earnings,
        includeDelta: hasPreviousRange && !previous.isLoading,
      }),
    [current.data, previous.data, hasPreviousRange, previous.isLoading],
  );

  return {
    metric,
    isLoading: !isReady || current.isLoading,
    isComparisonLoading: hasPreviousRange && previous.isLoading,
    isError: current.isError || previous.isError,
    error: current.error ?? previous.error,
  };
}
